import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Icon } from '@hologram-dimension/icon';
import { Loader } from '@hologram-dimension/loader';
import { Select, SelectOptionItem } from '@hologram-dimension/select';
import { useSimDeviceOverview, useSimProfiles } from 'api/index';
import useSimProfileEnableCall from 'api/simActions/useSimProfileEnableCall';
import {
  ChangeEventHandler,
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  ErrorPkgHTTPError,
  ProfilesDaoSimProfileDetails,
} from 'types/__generated__/SimInventoryApi';
import { HologramSimIdsMap, OnSubmit, SuccessfulSwitch } from 'types/common';
import styles from './SimSwitchProfiles.module.css';

interface ProfilesProps {
  options: Array<SelectOptionItem>;
  onProfileChange: ChangeEventHandler<HTMLSelectElement>;
  pendingAction: boolean;
  toDisableProfile?: ProfilesDaoSimProfileDetails;
  selectedProfile?: ProfilesDaoSimProfileDetails;
  otherProfiles: Array<ProfilesDaoSimProfileDetails>;
  loading: boolean;
}

const Profiles: FC<ProfilesProps> = ({
  options,
  onProfileChange,
  pendingAction,
  selectedProfile,
  toDisableProfile,
  otherProfiles,
  loading,
}) => (
  <div className={styles.profiles}>
    <div className={styles.toDisable}>
      <div className={styles.header}>Profile to disable</div>
      <div className={styles.profileContent}>
        {toDisableProfile?.profileName && (
          <div className={styles.profileName}>{toDisableProfile?.profileName}</div>
        )}
        <div className={styles.iccid}>{toDisableProfile?.iccid}</div>
        {pendingAction && (
          <div className={styles.pending}>
            <Icon size="small" name="Warning" fill="DdsColorFillNotificationCaution" />
            Pending disable
          </div>
        )}
      </div>
    </div>
    <Icon
      name="ArrowEastLong"
      fill="DdsColorPaletteGrey60"
      className={styles.iconEast}
      size="medium"
    />
    <div className={styles.toEnable}>
      <div className={styles.header}>Profile to enable</div>
      <div className={styles.profileContent}>
        {otherProfiles.length > 1 && !pendingAction ? (
          <Select
            id="ProfileSelect"
            options={options}
            inputProps={{ onChange: onProfileChange }}
            defaultValue={selectedProfile?.simProfileId?.toString()}
            disabled={loading}
          />
        ) : (
          selectedProfile?.profileName && (
            <div className={styles.profileName}>{selectedProfile?.profileName}</div>
          )
        )}
        <div className={styles.iccid}>{selectedProfile?.iccid}</div>
        {pendingAction && (
          <div className={styles.pending}>
            <Icon size="small" name="Warning" fill="DdsColorFillNotificationCaution" />
            Pending enable
          </div>
        )}
      </div>
    </div>
  </div>
);

const mapApiErrorToDisplay = (apiError?: string) => {
  switch (apiError) {
    case 'Profile is not downloaded':
      return 'The profile you are trying to enable is not available on this SIM. If the target profile was installed recently, the installation may not be complete. If this error persists, please contact support.';

    case 'SIM already has a different operation queued':
      return 'Unable to switch profiles at this time. Another eUICC operation is already queued for this SIM. If this error persists, please contact support.';

    default:
      return 'Unable to switch profiles at this time. Another eUICC operation may be queued or we may be experiencing system issues. If this error persists, please contact support.';
  }
};

interface SimSwitchProfilesProps {
  sims: HologramSimIdsMap;
  onProcessing: (value: boolean) => void;
  onDone: (sims?: Array<SuccessfulSwitch>, error?: string) => void;
}

const SimSwitchProfiles = forwardRef<OnSubmit, SimSwitchProfilesProps>(
  ({ sims, onProcessing, onDone }, ref) => {
    // Only one SIM at a time for now
    const hologramSimId = sims.size >= 1 ? [...sims.keys()][0] : undefined;

    const {
      simProfiles,
      isLoading: simProfilesLoading,
      error: simProfilesError,
      mutate: mutateSimProfiles,
      isValidating: simProfilesValidating,
    } = useSimProfiles(hologramSimId);

    // Force fresh data on component mount
    useEffect(() => {
      mutateSimProfiles();
    }, [mutateSimProfiles]);

    const { deviceOverview } = useSimDeviceOverview(hologramSimId);

    const { toDisableProfile, otherProfiles, pendingAction } = useMemo(() => {
      const enabledProfile = simProfiles.find((profile) => profile.installationState === 'ENABLED');

      const anyPending = !!simProfiles.find((profile) =>
        profile.installationState?.includes('PENDING')
      );

      const notEnabled = simProfiles.filter(
        (profile) => profile.simProfileId !== enabledProfile?.simProfileId
      );

      return {
        toDisableProfile: enabledProfile,
        otherProfiles: anyPending
          ? [
              simProfiles.find((profile) => profile.installationState === 'ENABLE_PENDING') ??
                notEnabled[0],
            ]
          : notEnabled,
        pendingAction: anyPending,
      };
    }, [simProfiles]);

    const [selectedProfile, setSelectedProfile] = useState<
      ProfilesDaoSimProfileDetails | undefined
    >(undefined);

    // Set the default profile
    useEffect(() => {
      if (otherProfiles.length === 0 || selectedProfile) {
        return;
      }
      setSelectedProfile(otherProfiles[0]);
    }, [otherProfiles, selectedProfile]);

    const onProfileChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
      (e) => {
        const newProfile = otherProfiles.find(
          (profile) => profile.simProfileId?.toString() === e.target.value
        );
        setSelectedProfile(newProfile);
      },
      [otherProfiles]
    );

    const [actionError, setActionError] = useState<string | undefined>();
    const enableProfileApi = useSimProfileEnableCall({
      hologramSimId,
      simProfileId: selectedProfile?.simProfileId,
    });

    const [apiActionProcessing, setApiActionProcessing] = useState(false);

    const error = simProfilesError?.message || actionError;

    // The SIM Profiles loading state should almost never occur, since simply visiting the SIM details page will prime the cache.
    // That said, this is a component and we handle this properly to avoid issues re-using the component.
    // But the API gets mutated when the modal is opened, so we need to handle that.
    const isLoading = simProfilesLoading || simProfilesValidating;

    useEffect(() => {
      onProcessing(isLoading || apiActionProcessing);
    }, [apiActionProcessing, isLoading, onProcessing]);

    useEffect(() => {
      if (simProfiles.length < 2) {
        setActionError('SIM is not eligible for profile switch.');
      } else if (pendingAction) {
        setActionError(
          'This SIM is already waiting on a profile switch. Requesting more profile switch actions may cause unexpected configuration and connectivity issues.'
        );
      }
    }, [pendingAction, simProfiles]);

    const commitStateChange = useCallback(async () => {
      if (!hologramSimId) {
        setActionError('No selected SIM');
        return;
      }

      setApiActionProcessing(true);

      try {
        await enableProfileApi();

        onDone(
          [...sims.keys()].map<SuccessfulSwitch>((simId) => ({
            hologramSimId: simId,
            newProfileName: selectedProfile?.profileName ?? '',
            deviceName: deviceOverview.deviceName ?? '',
          }))
        );

        // Mutate simProfileDetails to update the selected profile's installation state to ENABLE_PENDING
        // This is a workaround to avoid the delay in the API response
        mutateSimProfiles(
          () => ({
            simProfileDetails: simProfiles.map((profile) => {
              if (selectedProfile?.simProfileId === profile.simProfileId) {
                return {
                  ...profile,
                  installationState: 'ENABLE_PENDING',
                };
              }
              return profile;
            }),
          }),
          {
            revalidate: false,
          }
        );
      } catch (ex) {
        if (typeof ex === 'string') {
          setActionError(ex);
        } else if ((ex as any).info) {
          const errorApi: ErrorPkgHTTPError = (ex as any).info;
          setActionError(`An error occurred: ${mapApiErrorToDisplay(errorApi.message)}`);

          // eslint-disable-next-line no-console
          console.error(ex);
        } else {
          setActionError(`An error occurred: ${ex ?? 'Unknown'}`);

          // eslint-disable-next-line no-console
          console.error(ex);
        }
      }

      setApiActionProcessing(false);
    }, [
      hologramSimId,
      enableProfileApi,
      onDone,
      sims,
      selectedProfile,
      deviceOverview,
      mutateSimProfiles,
      simProfiles,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: commitStateChange,
      }),
      [commitStateChange]
    );

    useEffect(() => {
      if (error) {
        onDone(undefined, error);
      }
    }, [error, onDone]);

    const options: Array<SelectOptionItem> = useMemo(
      () =>
        otherProfiles
          .filter((profile) => profile.simProfileId && (profile.profileName || profile.iccid))
          .map((profile) => ({
            value: profile.simProfileId?.toString() as string,
            label: profile.profileName ?? (profile.iccid as string),
          })),
      [otherProfiles]
    );

    let content;
    if (isLoading) {
      content = <Loader>Checking selected SIMs...</Loader>;
    } else {
      content = (
        <div className={styles.content}>
          {error && <BannerNotification variant="error">{error}</BannerNotification>}
          <div className={styles.helpText}>
            <p>
              Switching SIM profiles will change network providers, changing the carriers and
              coverage available to your device.
            </p>
            <ul>
              <li>Profile changes take effect the next time the SIM connects to a network.</li>
              <li>
                After the SIM profile changes, SIMs will be ready for use on our network within 10
                minutes. In rare cases it may take up to 24 hours.
              </li>
            </ul>
          </div>
          <Profiles
            options={options}
            onProfileChange={onProfileChange}
            pendingAction={pendingAction}
            selectedProfile={selectedProfile}
            toDisableProfile={toDisableProfile}
            otherProfiles={otherProfiles}
            loading={simProfilesLoading}
          />
        </div>
      );
    }

    return <div className={styles.wrapper}>{content}</div>;
  }
);

export default SimSwitchProfiles;
